import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import type { EventDropArg } from '@fullcalendar/core';
import {
  Alert,
  Badge,
  Button,
  Card,
  Page,
  PageHeader,
  Toast,
  showToast,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  businessDate,
  type AssistanceServiceType,
} from '@evogard/domain';
import type { DispatchRow } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { DispatchDesk } from './DispatchDesk.js';
import { AGENDA_KEY, ASSISTANCE_KEY, reais, when } from './assistance.js';

/** A cor de cada tipo no calendário — tokens da casa, nos dois temas. */
const TONE: Record<AssistanceServiceType, string> = {
  reboque: 'azul',
  bateria: 'amarelo',
  pneu: 'laranja',
  pane_seca: 'vermelho',
  chaveiro: 'roxo',
  residencial: 'verde',
  taxi: 'turquesa',
  hospedagem: 'ciano',
};

const HORIZON_DAYS = 62;

/**
 * A Agenda (`assistencia-21`) — os agendamentos ainda não executados nem
 * cancelados.
 *
 * O calendário é o FullCalendar 6, o mesmo do Safegard (`page/agenda.php`),
 * pintado pelos tokens da casa (`styles.css`). Clicar abre a janela do
 * acionamento, que executa, remarca e cancela; arrastar remarca. Nada é
 * enviado sozinho: depois de remarcar, a central copia o script de novo e
 * avisa o prestador (P13).
 */
export function AgendaScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const canCreate = can(context, 'assistance.dispatches', 'criar');
  const [view, setView] = useState<'calendario' | 'lista'>('calendario');
  const [open, setOpen] = useState<number | null>(null);

  const from = businessDate(new Date(Date.now() - 7 * 86_400_000));
  const to = businessDate(new Date(Date.now() + HORIZON_DAYS * 86_400_000));
  const agenda = useQuery({
    queryKey: [...AGENDA_KEY, tenantId, from, to],
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.agenda({ query: { from, to } }),
        200,
        'Não foi possível carregar a agenda.',
      ),
  });

  const move = useMutation({
    mutationFn: async ({ id, at }: { id: number; at: Date }) =>
      ok(
        await api.assistanceDispatches.updateScheduled({
          params: { id },
          body: { scheduledFor: at.toISOString() },
        }),
        200,
        'Não foi possível remarcar.',
      ),
    onSuccess: (d) => {
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
      showToast((dismiss) => (
        <Toast title={`${d.code} remarcado`} icon="calendario" onClose={dismiss}>
          <p className="text-sm">
            Para {when(d.scheduledFor)}. Copie o script na janela do acionamento e avise o
            prestador da nova data.
          </p>
        </Toast>
      ));
    },
  });

  const items = agenda.data ?? [];
  const now = Date.now();
  const events = items.map((d) => {
    const tone = TONE[d.serviceType];
    const start = new Date(d.scheduledFor!);
    return {
      id: String(d.id),
      start,
      end: new Date(start.getTime() + 60 * 60_000),
      title: `${ASSISTANCE_SERVICES.find((s) => s.type === d.serviceType)?.shortLabel} · ${d.memberName ?? ''} · ${d.plate ?? ''}`,
      backgroundColor: `var(--color-fase-${tone}-suave)`,
      borderColor: `var(--color-fase-${tone})`,
      textColor: 'var(--color-texto)',
      classNames: start.getTime() < now ? ['passou'] : [],
      editable: canCreate,
    };
  });

  const onDrop = (info: EventDropArg) => {
    if (!info.event.start) return info.revert();
    move.mutate(
      { id: Number(info.event.id), at: info.event.start },
      { onError: () => info.revert() },
    );
  };

  return (
    <Page measure="cheia">
      <PageHeader
        title="Agenda"
        actions={
          <div className="flex gap-2">
            <Button
              variant="secundario"
              onClick={() => setView(view === 'calendario' ? 'lista' : 'calendario')}
            >
              {view === 'calendario' ? 'Ver em lista' : 'Ver no calendário'}
            </Button>
            {canCreate && (
              <Link to="/assistance/dispatches" search={{ novo: 'agendar' }}>
                <Button icon="calendario">Agendar acionamento</Button>
              </Link>
            )}
          </div>
        }
      >
        Acionamentos marcados para depois. Ficam salvos com tudo e o prestador é avisado
        pela central, mas não contam: aparecem em Acionamentos com o status Agendado, não
        têm o link das fotos e não entram no pagamento até alguém executar.
      </PageHeader>
      {(agenda.error ?? move.error) && (
        <Alert tone="erro">{(agenda.error ?? move.error)!.message}</Alert>
      )}

      {view === 'calendario' ? (
        <Card
          title="Calendário"
          descricao="Clique para executar, remarcar ou cancelar. Arraste para remarcar. Tracejado: o horário passou."
        >
          <div className="agenda-calendario">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              locale={ptBrLocale}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              buttonText={{ today: 'Hoje', month: 'Mês', week: 'Semana', day: 'Dia' }}
              height={620}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              scrollTime="07:00:00"
              allDaySlot={false}
              nowIndicator
              eventDisplay="block"
              dayMaxEvents={3}
              eventDurationEditable={false}
              events={events}
              eventClick={(info) => setOpen(Number(info.event.id))}
              eventDrop={onDrop}
            />
          </div>
        </Card>
      ) : (
        <AgendaList items={items} onOpen={setOpen} />
      )}
      {open !== null && <DispatchDesk id={open} onClose={() => setOpen(null)} />}
    </Page>
  );
}

function AgendaList({
  items,
  onOpen,
}: {
  items: DispatchRow[];
  onOpen: (id: number) => void;
}) {
  if (items.length === 0) {
    return (
      <Card title="Nada agendado">
        Nenhum agendamento a executar nos próximos dois meses.
      </Card>
    );
  }
  const days = new Map<string, DispatchRow[]>();
  for (const d of items) {
    const day = businessDate(new Date(d.scheduledFor!));
    days.set(day, [...(days.get(day) ?? []), d]);
  }
  return (
    <div className="flex flex-col gap-4">
      {[...days.entries()].map(([day, list]) => (
        <Card
          key={day}
          title={`${day.slice(8, 10)}/${day.slice(5, 7)}`}
          descricao={`${list.length} agendamento(s)`}
        >
          <ul className="flex flex-col divide-y divide-borda">
            {list.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <b className="w-14 tabular-nums">{when(d.scheduledFor).slice(-5)}</b>
                {new Date(d.scheduledFor!).getTime() < Date.now() && (
                  <Badge tone="alerta">Horário passou</Badge>
                )}
                <span className="min-w-0 flex-1">
                  <b>
                    {
                      ASSISTANCE_SERVICES.find((s) => s.type === d.serviceType)
                        ?.shortLabel
                    }
                  </b>{' '}
                  · {d.memberName} · {d.plate}
                  <span className="block text-xs text-texto-suave">
                    {d.originLabel}
                    {d.destinationLabel ? ` → ${d.destinationLabel}` : ''} ·{' '}
                    {d.providerName}
                  </span>
                </span>
                <span className="tabular-nums">{reais(d.amount)}</span>
                <Button size="sm" variant="secundario" onClick={() => onOpen(d.id)}>
                  Abrir
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
